import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'src/components/Button';
import dialogController from 'src/controller/dialogController';
import Modal from 'src/components/Modal';

import styles from './ReservationConfirm.module.scss';
import ProgressiveImage from '../ProgressiveImage';

interface Props {
  type: 'reservation' | 'cancel';
  coach: string;
  date: string;
  startTime: string;
  endTime: string;
  onConfirm: () => void | Promise<void>;
  onClose?: () => void;
}

const ReservationConfirm = ({ type, coach, date, startTime, endTime, onConfirm, onClose = () => {} }: Props) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const { t } = useTranslation('schedule');
  const { t: tGeneral } = useTranslation();
  const lowerCoach = useMemo(() => coach.toLowerCase(), [coach]);
  const handleConfirm = useCallback(async () => {
    if (isProcessing) return;
    try {
      setIsProcessing(true);
      await onConfirm();
      setIsProcessing(false);
      onClose();
    } catch (e) {
      setIsProcessing(false);
    }
  }, [isProcessing, onConfirm, onClose]);
  return (
    <Modal onClose={onClose}>
      <div className={styles.container}>
        {lowerCoach !== 'special' && (
          <div className={styles.coach}>
            <ProgressiveImage
              urls={[
                `/static/assets/img/coach/reservation-${lowerCoach}-s.png`,
                `/static/assets/img/coach/reservation-${lowerCoach}-m.png`,
                `/static/assets/img/coach/reservation-${lowerCoach}.jpg`,
              ]}
              alt={coach}
            />
          </div>
        )}
        <div className={styles.content}>
          <div>
            <div className={styles.title}>
              {type === 'reservation' ? t('reservation-confirm') : t('reservation-cancel-confrim')}
            </div>
            <div className={styles.subtitle}>{`${date} ${startTime} ~ ${endTime}`}</div>
          </div>
          <div>
            <div className={styles.terms}>
              <div>{t('terms-reservation')}</div>
              <div>{t('terms-reservation-1')}</div>
              <div>{t('terms-cancellation')}</div>
              <div>{t('terms-cancellation-1')}</div>
              <div>{t('terms-cancellation-2')}</div>
              <div>{t('terms-cancellation-3')}</div>
              <div>{t('terms-refund')}</div>
              <div>{t('terms-refund-1')}</div>
            </div>
            <div className={styles.buttons}>
              <Button disabled={isProcessing} className={styles.button} onClick={onClose}>
                {tGeneral('cancel')}
              </Button>
              <Button type="primary" className={styles.button} disabled={isProcessing} onClick={handleConfirm}>
                {tGeneral('confirm')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export const showReservationConfirm = ({ type, coach, date, startTime, endTime, onConfirm }: Props) => {
  const id = `reservation-confirm-${type}`;
  const onClose = () => dialogController.popById(id);
  dialogController.addDialog({
    id,
    component: (
      <ReservationConfirm
        type={type}
        date={date}
        coach={coach}
        startTime={startTime}
        endTime={endTime}
        onConfirm={onConfirm}
        onClose={onClose}
      />
    ),
  });
};

export default ReservationConfirm;
